import { Button } from 'antd';

import { WARNING } from '../icons';
import { ID_CHECK_STATUS, isFlagged } from '../../utils/idValueValidation';

/**
 * The builders' flag for a value with an id behind it (ruling d21;
 * utils/idValueValidation.js holds the rules and the words).
 *
 * One line per check, directly under the value it is about: the plain
 * sentence, then the offered matches as small buttons — one click writes that
 * match in place of what was typed. A flag (unknown, ambiguous) reads in the
 * warning ink with its glyph; a title match on an item lookup is not a flag
 * and reads as a quiet note. Nothing here holds Apply or Save.
 *
 *   checks      [{ result, member?, typed }] — from checkConditionValues or
 *               checkQueryValues, already passed through displayedCheck
 *   onPick      (value, check) => void; without it the matches are not offered
 *   fieldLabel  prefixes each line ("Status: …") where the line is not beside
 *               its row — a text mode's list; a check's own `fieldLabel` wins
 *   disabled    the builder's own disabled state
 *
 * On a touch screen each match keeps the 44px floor (builders.css), and the
 * line wraps: nothing scrolls sideways at 375px.
 */
export default function IdValueCheck({ checks = [], onPick, fieldLabel, disabled = false, className }) {
	const shown = (checks || []).filter(check => check?.result?.message);
	if (!shown.length) return null;
	return (
		<div className={`id-check${className ? ` ${className}` : ''}`} role="status">
			{shown.map((check, index) => {
				const { result } = check;
				const flagged = isFlagged(result);
				const matches = typeof onPick === 'function' ? (result.matches || []) : [];
				const lead = result.status === ID_CHECK_STATUS.TITLE_MATCH && matches.length ? result.matchesLead : null;
				const label = check.fieldLabel ?? fieldLabel;
				return (
					<div
						key={`${check.member ?? ''}:${check.typed ?? ''}:${index}`}
						className={`id-check__line id-check__line--${flagged ? 'flag' : 'note'}`}
					>
						<span className="id-check__message">
							{flagged ? <WARNING className="id-check__glyph" aria-hidden="true" /> : null}
							{label ? <span className="id-check__field">{label}: </span> : null}
							{result.message}
							{lead ? ` ${lead}` : ''}
						</span>
						{matches.length ? (
							<span className="id-check__matches">
								{matches.map(match => (
									<Button
										key={match.value}
										size="small"
										className="id-check__match"
										disabled={disabled}
										aria-label={`Use ${match.label}${match.detail ? ` (${match.detail})` : ''}`}
										onClick={() => onPick(match.value, check)}
									>
										{match.label}
										{match.detail ? <span className="id-check__detail">{match.detail}</span> : null}
									</Button>
								))}
							</span>
						) : null}
					</div>
				);
			})}
		</div>
	);
}
